import { Component, OnInit, signal, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastService } from 'angular-toastify';
import { PssLoadingComponent } from '../../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ParkingSite } from '../../../../../core/models/parking/parking-site.model';
import { ApiParkingSiteService } from '../../../../../core/services/api/parking/api-parking-site.service';
import { MapperParkingSiteService } from '../../../../../core/mappers/parking/mapper-parking-site.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { ModalParkingSiteComponent } from '../../../modals/parking/modal-parking-site/modal-parking-site.component';

@Component({
  selector: 'app-parking-site',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalParkingSiteComponent],
  templateUrl: './parking-site.component.html',
  styleUrl: './parking-site.component.css',
})
export class ParkingSiteComponent implements OnInit {
  private readonly apiService = inject(ApiParkingSiteService);
  private readonly mapper = inject(MapperParkingSiteService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  sites = signal<ParkingSite[]>([]);
  isLoading = signal(false);
  searchText = signal('');
  currentPage = signal(1);
  pageSize = signal(20);
  pageSizeOptions = [10, 20, 50, 100];
  totalItems = signal(0);
  totalPages = signal(0);

  isModalOpen = signal(false);
  selectedId = signal(0);

  private searchDebounce: ReturnType<typeof setTimeout> | null = null;

  get canCreate(): boolean {
    return this.permissionService.canCreate(this.router.url);
  }

  get canUpdate(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  get startItemIndex(): number {
    if (this.totalItems() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage() * this.pageSize(), this.totalItems());
  }

  get pagesArray(): number[] {
    const total = this.totalPages();
    const maxVisibleButtons = 5;
    let startPage = Math.max(1, this.currentPage() - 2);
    let endPage = Math.min(total, startPage + maxVisibleButtons - 1);
    if (endPage - startPage < maxVisibleButtons - 1) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  ngOnInit(): void {
    this.loadSites();
  }

  onRefresh(): void {
    this.currentPage.set(1);
    this.loadSites();
  }

  onSearchChange(): void {
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
    this.searchDebounce = setTimeout(() => {
      this.currentPage.set(1);
      this.loadSites();
    }, 350);
  }

  openCreate(): void {
    if (!this.canCreate) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }
    this.selectedId.set(0);
    this.isModalOpen.set(true);
  }

  openEdit(site: ParkingSite): void {
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    this.selectedId.set(site.id);
    this.isModalOpen.set(true);
  }

  closeModal(): void {
    this.isModalOpen.set(false);
    this.selectedId.set(0);
  }

  onSaveSuccess(): void {
    this.loadSites();
  }

  openGoogleMaps(site: ParkingSite): void {
    if (site.latitude == null || site.longitude == null) {
      this.toast.info('ไม่มีพิกัด Latitude/Longitude');
      return;
    }
    window.open(`https://www.google.com/maps?q=${site.latitude},${site.longitude}`, '_blank', 'noopener,noreferrer');
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
      this.loadSites();
    }
  }

  onPageSizeChange(event: Event): void {
    const element = event.target as HTMLSelectElement;
    this.pageSize.set(Number(element.value));
    this.currentPage.set(1);
    this.loadSites();
  }

  trackById(_index: number, item: ParkingSite): number {
    return item.id;
  }

  logoSrc(site: ParkingSite): string | null {
    return this.mapper.toLogoSrc(site.logoBase64);
  }

  private loadSites(): void {
    this.isLoading.set(true);
    const text = this.searchText().trim() || undefined;

    this.apiService.getParkingSiteForTable({
      page: this.currentPage(),
      pageSize: this.pageSize(),
      searchText: text,
      sortColumn: 'id',
      sortDirection: 'asc',
    }).subscribe({
      next: (res) => {
        this.sites.set((res.data || []).map(d => this.mapper.mapFromDto(d)));
        this.totalItems.set(res.total ?? 0);
        this.totalPages.set(res.totalPages ?? 0);
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error(err);
        this.toast.error('ไม่สามารถโหลดข้อมูล Parking Site ได้');
        this.isLoading.set(false);
      },
    });
  }
}
